class ProductTooltip extends HTMLElement {
  constructor() {
    super();

    this.initTooltip();
    window.addEventListener('variant:change', this.initTooltip.bind(this));
  }

  showToolTipDrawer() {
    document.body.classList.add('show-tooltip-drawer');
    document.querySelector('[data-tooltip-trigger]').setAttribute('aria-expanded', 'true');
  }

  hideToolTipDrawer() {
    document.body.classList.remove('show-tooltip-drawer');
    document.querySelector('[data-tooltip-trigger]').setAttribute('aria-expanded', 'false');
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      hideToolTipDrawer();
    }
  }

  initTooltip() {
    document.querySelector('[data-tooltip-trigger]').addEventListener('click', () => {
      this.showToolTipDrawer();

      window.addEventListener('keydown', this.handleKeyDown.bind(this));
    });

    document.querySelectorAll('[data-tooltip-close]').forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.hideToolTipDrawer();

        window.removeEventListener('keydown', this.handleKeyDown.bind(this));
      });
    });
  }
}

if (!window.customElements.get('product-tooltip')) {
  window.customElements.define('product-tooltip', ProductTooltip);
}

class FreeGift extends HTMLElement {
  static STORAGE_KEY = 'freegift_declined';

  constructor() {
    super();
    this.hasFreeGift = false;
    this.previousItems = new Map(); // key -> quantity for non-gift items
  }

  get declined() {
    return localStorage.getItem(FreeGift.STORAGE_KEY) === '1';
  }

  set declined(value) {
    if (value) {
      localStorage.setItem(FreeGift.STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(FreeGift.STORAGE_KEY);
    }
  }

  connectedCallback() {
    this.options = JSON.parse(this.getAttribute('data-options'));
    this.enabled = this.options.enabled;
    this.threshold = this.options.threshold;
    this.productId = this.options.product;

    this.handleCartChange = this.handleCartChange.bind(this);
    document.addEventListener('cart:change', this.handleCartChange);
    document.addEventListener('DOMContentLoaded', this.initFreeGift.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.handleCartChange);
  }

  findGiftItem(items) {
    return items.find(item => item.id === this.productId && item.properties?._free_gift);
  }

  findAnyGiftItem(items) {
    return items.find(item => item.properties?._free_gift);
  }

  initFreeGift() {
    this.cart = this.options.cart;
    const totalPrice = this.cart.total_price;

    // If feature is disabled, remove any stale free gift items and stop
    if (!this.enabled) {
      const staleGift = this.findAnyGiftItem(this.cart.items);
      if (staleGift) this.removeProductFromCart(staleGift.key);
      return;
    }

    const freeGiftItem = this.findGiftItem(this.cart.items);
    this.hasFreeGift = !!freeGiftItem;
    this.previousItems = new Map(
      this.cart.items.filter(item => !item.properties?._free_gift).map(item => [item.key, item.quantity])
    );

    if (totalPrice >= this.threshold) {
      if (this.hasFreeGift) return;
      if (this.declined) return;
      this.addProductToCart();
    } else {
      if (!this.hasFreeGift) return;
      this.removeProductFromCart(freeGiftItem.key);
    }
  }

  handleCartChange(event) {
    if (!event.detail.cart) return;

    const cart = event.detail.cart;
    const totalPrice = cart.total_price;

    // If feature is disabled, remove any stale free gift items and stop
    if (!this.enabled) {
      const staleGift = this.findAnyGiftItem(cart.items);
      if (staleGift) this.removeProductFromCart(staleGift.key);
      return;
    }

    const freeGiftItem = this.findGiftItem(cart.items);
    const currentlyHasFreeGift = !!freeGiftItem;

    // Detect new item added or existing item quantity increased
    const currentItems = new Map(
      cart.items.filter(item => !item.properties?._free_gift).map(item => [item.key, item.quantity])
    );
    const cartIncreased = [...currentItems].some(([key, qty]) =>
      !this.previousItems.has(key) || qty > this.previousItems.get(key)
    );

    if (cartIncreased) {
      this.declined = false;
    }

    // Detect customer-initiated removal: gift was present, now it's gone, still above threshold
    if (totalPrice >= this.threshold && this.hasFreeGift && !currentlyHasFreeGift) {
      this.declined = true;
    }

    this.hasFreeGift = currentlyHasFreeGift;
    this.previousItems = currentItems;

    // Cancel any pending add so rapid mutations (remove item → add item → remove gift)
    // settle before we decide to add the gift.
    clearTimeout(this._addGiftTimer);

    if (totalPrice >= this.threshold) {
      if (currentlyHasFreeGift || this.declined) return;

      // Debounce: re-check state after all cart events have fired
      this._addGiftTimer = setTimeout(() => {
        if (!this.hasFreeGift && !this.declined) {
          this.addProductToCart();
        }
      }, 300);
      return;
    } else {
      if (!currentlyHasFreeGift) return;
      this.removeProductFromCart(freeGiftItem.key);
    }
  }

  addProductToCart() {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
          'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items: [{ id: this.productId, quantity: 1, properties: { _free_gift: true } }] })
    })
    .then(response => response.json())
    .then(() => {
      this.hasFreeGift = true;
      if (document.querySelector('.shopify-section--main-cart')) {
        window.location.reload();
      }
      else {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));
      }
    })
    .catch((error) => {
        console.error('Error:', error);
    });
  }

  removeProductFromCart(key) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
          'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
    .then(response => response.json())
    .then(() => {
      if (document.querySelector('.shopify-section--main-cart')) {
        window.location.reload();
      }
      else {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));
      }
    })
    .catch((error) => {
      console.error('Error:', error);
    });
  }
}

if (!window.customElements.get('free-gift')) {
  window.customElements.define('free-gift', FreeGift);
}

// Dynamic page dots — compact 5-dot sliding window, compatible with CarouselNavigation.
// Wraps buttons in a .page-dots__track span (after theme's connectedCallback has already
// cached button references), then translates the track to keep the active dot in view.
class DynamicPageDots {
  static MAX_VISIBLE = 5;
  static HALF = Math.floor(DynamicPageDots.MAX_VISIBLE / 2);

  constructor(nav) {
    this.nav = nav;
    this.track = null;
    this.slotWidth = 0;
    this.init();
  }

  init() {
    const track = document.createElement('span');
    track.className = 'page-dots__track';
    while (this.nav.firstChild) {
      track.appendChild(this.nav.firstChild);
    }
    this.nav.appendChild(track);
    this.track = track;

    const carousel = document.getElementById(this.nav.getAttribute('aria-controls'));
    if (!carousel) return;

    requestAnimationFrame(() => {
      this.measureSlotWidth();
      this.onCarouselChange();
    });

    carousel.addEventListener('carousel:change', () => this.onCarouselChange());
    carousel.addEventListener('carousel:filter', () => this.onCarouselChange());
  }

  measureSlotWidth() {
    const buttons = this.visibleButtons();
    if (buttons.length < 2) return;
    this.slotWidth = buttons[1].offsetLeft - buttons[0].offsetLeft;
  }

  visibleButtons() {
    return Array.from(this.track.querySelectorAll('button:not([hidden])'));
  }

  onCarouselChange() {
    if (!this.slotWidth) this.measureSlotWidth();

    const buttons = this.visibleButtons();
    if (!buttons.length) return;

    const activeIndex = buttons.findIndex(b => b.getAttribute('aria-current') === 'true');
    if (activeIndex === -1) return;

    const N = buttons.length;
    const V = DynamicPageDots.MAX_VISIBLE;
    const HALF = Math.floor(V / 2);

    if (N <= V) {
      this.track.style.transform = '';
      return;
    }

    let windowStart;
    if (activeIndex <= HALF) {
      windowStart = 0;
    } else if (activeIndex >= N - HALF - 1) {
      windowStart = N - V;
    } else {
      windowStart = activeIndex - HALF;
    }

    this.track.style.transform = `translateX(${-(windowStart * this.slotWidth)}px)`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  customElements.whenDefined('carousel-navigation').then(() => {
    document.querySelectorAll('carousel-navigation.page-dots--dynamic').forEach(nav => {
      new DynamicPageDots(nav);
    });
  });
});

// The following code listens for the 'variant:change' event and updates the content of the element with the ID 'product-extra-information' based on the HTML fragment provided in the event detail. This allows for dynamic updating of product information when a variant is changed.
window.addEventListener('variant:change', (event) => {
  if (!event.detail.htmlFragment) return;

  if (document.getElementById('product-extra-information') && event.detail.htmlFragment.getElementById('product-extra-information')) {
    document.getElementById('product-extra-information').innerHTML = event.detail.htmlFragment.getElementById('product-extra-information').innerHTML;
  }

  if (document.getElementById('product-info-accordions') && event.detail.htmlFragment.getElementById('product-info-accordions')) {
    document.getElementById('product-info-accordions').innerHTML = event.detail.htmlFragment.getElementById('product-info-accordions').innerHTML;

    document.querySelectorAll('[data-variant-metafield-accordion] details').forEach(details => {
      details.open = true;
    });
  }
});